import { API_BASE_URL, ApiError, classifiedFetch, parseError, silentRefresh, apiFetch, type TokenStore } from './client.ts';
import { SIGNED_OUT_MESSAGE } from '../lib/error-labels.ts';

/**
 * Upload a file to a practice (POST /practices/:id/files). The API has no
 * multipart parser, so the file is sent base64-encoded in a JSON body. Returns
 * the new file_object id - used to satisfy the file_object_id a contract or
 * document template requires. Suitable for small files (templates); the route
 * caps the body at a few MB.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string; // "data:<mime>;base64,XXXX"
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadPracticeFile(
  practiceId: string,
  file: File,
  store: TokenStore,
): Promise<{ fileObjectId: string; sha256: string; byteSize: number }> {
  const contentBase64 = await fileToBase64(file);
  return apiFetch<{ fileObjectId: string; sha256: string; byteSize: number }>(
    `/practices/${encodeURIComponent(practiceId)}/files`,
    {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
      }),
    },
    store,
  );
}

/**
 * Authenticated file download (spec §10 line 1558; invariant 14 - all file
 * access goes through GET /files/:id, which logs access server-side; the UI
 * never bypasses it with a storage URL, and no storage_key is ever exposed).
 *
 * The access token lives in memory (not a cookie), so a plain `<a href>` to
 * /files/:id would be unauthenticated. We fetch the bytes WITH the bearer token,
 * wrap them in an object URL, and trigger a download via a synthesised
 * `<a download>` - the iOS-Safari-safe fallback (no reliance on a direct link).
 */
export async function downloadFileObject(
  fileObjectId: string,
  filename: string,
  store: TokenStore,
): Promise<void> {
  return downloadAuthenticatedFile(`/files/${encodeURIComponent(fileObjectId)}`, filename, store);
}

/** Same fetch-with-bearer-token + object-URL trick, for any authenticated
 *  download path (e.g. a registration/endorsement certificate route). */
export async function downloadAuthenticatedFile(
  path: string,
  filename: string,
  store: TokenStore,
): Promise<void> {
  const fetchOnce = (token: string | null): Promise<Response> =>
    classifiedFetch(`${API_BASE_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }, { timeoutMs: 300_000 });

  let res = await fetchOnce(store.getToken());
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      store.onRefresh(refreshed);
      res = await fetchOnce(refreshed.accessToken);
    } else {
      // Refresh failed - clear auth so the app routes back to login (matches
      // apiFetch's onAuthFailure path; PR 11a review tidy).
      store.onAuthFailure();
    }
  }
  if (res.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', SIGNED_OUT_MESSAGE);
  }
  if (!res.ok) {
    throw await parseError(res);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
